import React, { useState, useEffect } from 'react';
import { TOKENS } from '../tokens';
import api from '../api/client';
import { CheckCircle2, Clock, PlayCircle, Plus, Trash2 } from 'lucide-react';

export function TasksScreen() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', employeeId: '', location: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      // For now we fetch standard tasks; in a fuller implementation, this would fetch pm_tasks or tasks with project_id
      const { data } = await api.get('/tasks/all');
      // filter to only those that have a project_id (if your backend returns it) or just show all for now
      const resData = data.data || data;
      setTasks(Array.isArray(resData) ? resData : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const pwd = window.prompt("Enter admin password to delete this task:");
    if (pwd !== "123456") {
      alert("Incorrect password. Deletion cancelled.");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    try {
      await api.delete(`/tasks/${id}`);
      setTasks(tasks.filter(t => t.id !== id));
    } catch (err) {
      console.error(err);
      alert("Failed to delete task");
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/tasks', { 
        title: newTask.title, 
        employeeId: newTask.employeeId, 
        location: newTask.location 
      });
      alert("Task Assigned! An OD Form has also been automatically generated.");
      setIsModalOpen(false);
      setNewTask({ title: '', employeeId: '', location: '' });
      fetchTasks();
    } catch (err) {
      console.error(err);
      alert("Failed to assign task");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontFamily: 'Fraunces', color: TOKENS.navyDeep }}>Project Tasks</h2>
        <button 
          onClick={() => setIsModalOpen(true)}
          style={{
            background: TOKENS.navyDeep, color: '#fff', 
            border: 'none', padding: '10px 16px', borderRadius: 8, 
            fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer'
          }}>
          <Plus size={18} /> Assign Task
        </button>
      </div>

      {loading ? (
        <p>Loading tasks...</p>
      ) : tasks.length === 0 ? (
        <div style={{ background: '#fff', padding: 40, borderRadius: 16, textAlign: 'center', color: '#666' }}>
          <CheckCircle2 size={48} style={{ opacity: 0.2, marginBottom: 16 }} />
          <h3>No tasks found</h3>
          <p>Assign tasks to your team members for project delivery.</p>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '1px solid #eee', textAlign: 'left', color: '#666', fontSize: 13 }}>
                <th style={{ padding: '16px 20px' }}>Task Title</th>
                <th style={{ padding: '16px 20px' }}>Assignee</th>
                <th style={{ padding: '16px 20px' }}>Status</th>
                <th style={{ padding: '16px 20px' }}>Due Date</th>
                <th style={{ padding: '16px 20px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map(t => (
                <tr key={t.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '16px 20px', fontWeight: 500, color: TOKENS.navyDeep }}>{t.title}</td>
                  <td style={{ padding: '16px 20px', color: '#555' }}>{t.employee_id}</td>
                  <td style={{ padding: '16px 20px' }}>
                    <span style={{ 
                      background: t.status === 'completed' ? '#E6F4EA' : '#F3F4F6',
                      color: t.status === 'completed' ? '#1E8E3E' : '#4B5563',
                      padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4
                    }}>
                      {t.status === 'completed' ? <CheckCircle2 size={14} /> : <Clock size={14} />}
                      {t.status}
                    </span>
                  </td>
                  <td style={{ padding: '16px 20px', color: '#888', fontSize: 14 }}>
                    {t.due_date || t.time || 'No date'}
                  </td>
                  <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                    <button 
                      onClick={() => handleDelete(t.id)}
                      style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px' }}
                      title="Delete Task"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 400, padding: 24 }}>
            <h3 style={{ margin: '0 0 16px 0', color: TOKENS.navyDeep }}>Assign Project Task</h3>
            <form onSubmit={handleAssign}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500 }}>Task Title</label>
                <input required value={newTask.title} onChange={e => setNewTask({...newTask, title: e.target.value})} style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8 }} placeholder="E.g. Server Maintenance" />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500 }}>Assignee (Employee ID)</label>
                <input required value={newTask.employeeId} onChange={e => setNewTask({...newTask, employeeId: e.target.value})} style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8 }} placeholder="E.g. EMP001" />
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500 }}>Location</label>
                <input required value={newTask.location} onChange={e => setNewTask({...newTask, location: e.target.value})} style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8 }} placeholder="E.g. Data Center A" />
              </div>
              
              <p style={{ fontSize: 12, color: '#666', marginBottom: 20 }}>
                💡 An OD Form will be automatically generated for the assignee on today's date.
              </p>

              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" onClick={() => setIsModalOpen(false)} style={{ flex: 1, padding: 12, border: '1px solid #ddd', background: '#fff', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
                <button type="submit" disabled={submitting} style={{ flex: 1, padding: 12, border: 'none', background: TOKENS.gold, color: TOKENS.navyDeep, borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
                  {submitting ? 'Assigning...' : 'Assign Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
